import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useOrg } from '../app/OrgProvider';
import { AppLink } from '../components/Nav';
import {
  Badge,
  Banner,
  Button,
  CopyButton,
  EmptyState,
  ErrorText,
  Input,
  Loading,
  PageHeader,
  Spinner,
  cx,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import type { BrandingSettings, DnsRecord } from '../lib/types';
import { qk } from '../queries/keys';
import {
  checkCustomDomain,
  domainInFlight,
  domainLabel,
  domainPollMs,
  fetchBranding,
  removeCustomDomain,
  setCustomDomain,
} from '../queries/branding';
import { SettingsTabs } from './SettingsPage';

/**
 * Settings › Domain. Serving a team's share links from their own address.
 *
 * Its own tab rather than a section under Branding: the rest of branding is a
 * handful of choices somebody makes in a sitting, and this is a walk with
 * waiting in it, done once, usually by whoever looks after DNS rather than
 * whoever picked the logo. It also reads the same settings object, so opening
 * this tab after that one costs nothing.
 */

/**
 * Three stages, one record at a time. Which record is shown follows the
 * status, because showing both at once means the second is stale for as long
 * as the first takes.
 */
function DomainSection({
  branding,
  orgId,
  disabled,
  onSettled,
}: {
  branding: BrandingSettings;
  orgId: string;
  disabled: boolean;
  onSettled: (next: BrandingSettings) => void;
}) {
  const domain = branding.domain;
  const [host, setHost] = useState('');
  const label = domainLabel(domain.stage);

  const claim = useMutation({
    mutationFn: () => setCustomDomain(orgId, host.trim()),
    onSuccess: onSettled,
  });
  const check = useMutation({ mutationFn: () => checkCustomDomain(orgId), onSuccess: onSettled });
  const drop = useMutation({
    mutationFn: () => removeCustomDomain(orgId),
    onSuccess: (next) => {
      setHost('');
      onSettled(next);
    },
  });

  // While a domain is in flight the page asks again on its own, which is what
  // carries the walk from one stage to the next with nobody pressing anything.
  // Faster while the record is still being prepared, since that stage is ours
  // and usually over in a second or two; the API throttles what it actually
  // asks AWS, so watching closely costs nothing.
  useEffect(() => {
    if (!domainInFlight(domain.stage)) return;
    const timer = window.setInterval(() => {
      if (!check.isPending) check.mutate();
    }, domainPollMs(domain.stage));
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain.stage, check.isPending]);

  if (!domain.available) {
    return (
      <p className="text-caption text-chalk-faint">
        Custom domains are not available on this deployment.
      </p>
    );
  }

  if (!domain.host) {
    return (
      <form
        className="max-w-md space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (host.trim()) claim.mutate();
        }}
      >
        <Input
          label="Domain"
          placeholder="videos.acme.com"
          value={host}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setHost(event.target.value)}
          hint="A subdomain of a domain you own. You will add one DNS record to prove it is yours, and a second to point it at us."
        />
        <Button type="submit" variant="primary" size="sm" busy={claim.isPending} disabled={disabled || !host.trim()}>
          Add domain
        </Button>
        {claim.isError ? <ErrorText>{errorMessage(claim.error)}</ErrorText> : null}
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-body text-chalk">{domain.host}</span>
        <Badge tone={label.tone}>{label.title}</Badge>
        {domainInFlight(domain.stage) ? <Spinner size={14} className="text-chalk-faint" /> : null}
      </div>

      {domain.stage === 'live' ? (
        <Banner tone="go" title="Links use your domain">
          New links look like <span className="font-mono">https://{domain.host}/v/…</span>. Links already sent on the
          Slate address keep working.
        </Banner>
      ) : null}

      {domain.error ? <Banner tone="caution" title="Last check">{domain.error}</Banner> : null}

      {domain.stage === 'live' ? null : (
        <p className="text-caption text-chalk-dim">
          Two records at your DNS provider, in order. The first proves the domain is yours; the second sends it to
          us, and its value is only known once the first has been confirmed.
        </p>
      )}

      {/* Both steps, always, from the moment a domain is added. The second
          record's value cannot exist until the certificate is issued, but the
          fact that there is a second record can be said immediately, and
          saying it is what stops it arriving as a surprise after the first one
          has been done.

          Once the domain is serving, the walk is over and all of this is
          instructions for something already done, so it folds away. Not
          discarded: the records have to stay in DNS, the validation one
          because the certificate renews against it, so they are one click
          away rather than gone. */}
      <ol className={cx('space-y-3', domain.stage === 'live' && 'hidden')}>
        <DnsStep
          index={1}
          title="Prove the domain is yours"
          state={domain.stage === 'requesting' || domain.stage === 'dns' ? 'active' : 'done'}
          record={domain.validate}
          pending={domain.stage === 'requesting'}
          pendingLabel="Preparing this record. It usually takes a few seconds; nothing to do yet."
          note="Leave this one in place once it is added. It is checked again each time the certificate renews."
        />
        <DnsStep
          index={2}
          title="Point the domain at us"
          state={domain.stage === 'live' ? 'done' : domain.stage === 'building' ? 'active' : 'todo'}
          record={domain.point}
          pending={false}
          waitingLabel="The address to point at appears here once the first record is confirmed."
        />
      </ol>

      {domain.stage === 'live' && (domain.validate || domain.point) ? (
        <details className="rounded-md border border-hairline bg-slate-900/30 px-3 py-2">
          <summary className="cursor-pointer text-caption text-chalk-dim hover:text-chalk">
            DNS records for this domain
          </summary>
          <p className="mt-2 text-caption text-chalk-faint">
            Both are in place and nothing needs doing. Leave them there: the first is checked again each time the
            certificate renews, and the second is what sends the domain to us.
          </p>
          <dl className="mt-2 space-y-2">
            {[domain.validate, domain.point]
              .filter((record): record is DnsRecord => record !== null)
              .map((record) => (
                <div key={record.name} className="flex items-start gap-3">
                  <dt className="w-14 shrink-0 pt-1 text-caption text-chalk-faint">{record.type}</dt>
                  <dd className="min-w-0 flex-1">
                    <code className="block break-all font-mono text-caption text-chalk-dim">
                      {record.name} → {record.value}
                    </code>
                  </dd>
                </div>
              ))}
          </dl>
        </details>
      ) : null}

      <div className="flex items-center gap-3">
        <Button size="sm" busy={check.isPending} disabled={disabled} onClick={() => check.mutate()}>
          Check again
        </Button>
        <button
          type="button"
          className="text-caption text-chalk-dim hover:text-chalk disabled:opacity-40"
          disabled={disabled || drop.isPending}
          onClick={() => drop.mutate()}
        >
          {drop.isPending ? 'Removing…' : 'Remove domain'}
        </button>
      </div>
      {check.isError ? <ErrorText>{errorMessage(check.error)}</ErrorText> : null}
      {drop.isError ? <ErrorText>{errorMessage(drop.error)}</ErrorText> : null}
    </div>
  );
}

/**
 * One of the two DNS records, in its place in the walk. Drawn whether or not
 * its value is known yet: a step somebody cannot take now is still a step they
 * should be able to see coming.
 */
function DnsStep({
  index,
  title,
  state,
  record,
  pending,
  pendingLabel,
  waitingLabel,
  note,
}: {
  index: number;
  title: string;
  /** 'todo' is not reachable yet, 'active' is now, 'done' is behind them. */
  state: 'todo' | 'active' | 'done';
  record: DnsRecord | null;
  /** Ours to finish, rather than theirs: show why the value is not here yet. */
  pending: boolean;
  pendingLabel?: string;
  waitingLabel?: string;
  note?: string;
}) {
  return (
    <li
      className={cx(
        'rounded-md border p-3',
        state === 'active' ? 'border-hairline bg-slate-900/40' : 'border-hairline/60 bg-slate-900/20',
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={cx(
            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-micro font-medium',
            state === 'done' ? 'bg-go text-slate-950' : state === 'active' ? 'bg-chalk text-slate-950' : 'bg-slate-800 text-chalk-faint',
          )}
          aria-hidden
        >
          {state === 'done' ? '✓' : index}
        </span>
        <p className={cx('text-body', state === 'todo' ? 'text-chalk-dim' : 'text-chalk')}>{title}</p>
        {state === 'done' ? <span className="text-caption text-go">Done</span> : null}
        {pending ? <Spinner size={13} className="text-chalk-faint" /> : null}
      </div>

      {record ? (
        <>
          <dl className="mt-3 space-y-2">
            {[
              { label: 'Type', value: record.type },
              { label: 'Name', value: record.name },
              { label: 'Value', value: record.value },
            ].map((row) => (
              <div key={row.label} className="flex items-start gap-3">
                <dt className="w-14 shrink-0 pt-1 text-caption text-chalk-faint">{row.label}</dt>
                <dd className="flex min-w-0 flex-1 items-start gap-2">
                  <code className="min-w-0 flex-1 break-all rounded bg-slate-950 px-2 py-1 font-mono text-caption text-chalk">
                    {row.value}
                  </code>
                  <CopyButton value={row.value} label="Copy" copiedLabel="Copied" size="sm" />
                </dd>
              </div>
            ))}
          </dl>
          {note ? <p className="mt-2 text-caption text-chalk-faint">{note}</p> : null}
        </>
      ) : (
        <p className="mt-2 pl-[1.9rem] text-caption text-chalk-faint">
          {pending ? pendingLabel : waitingLabel}
        </p>
      )}
    </li>
  );
}

export function DomainPage() {
  const { org, loading: orgLoading } = useOrg();
  const client = useQueryClient();
  const orgId = org?.id ?? '';

  const query = useQuery({
    queryKey: qk.branding(orgId),
    queryFn: () => fetchBranding(orgId),
    enabled: orgId !== '',
  });
  const branding = query.data ?? null;
  const settled = (next: BrandingSettings) => client.setQueryData(qk.branding(orgId), next);

  if (orgLoading || (orgId !== '' && query.isLoading)) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="domain" />
        <Loading label="Loading your domain" />
      </>
    );
  }

  if (!org) {
    return (
      <>
        <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
        <SettingsTabs active="domain" />
        <EmptyState title="A domain belongs to a team">
          Share links on your own address come with the team plan, so there has to be a team first.
          <div className="mt-4">
            <AppLink to="/settings/subscription">
              <Button variant="primary">Start a team</Button>
            </AppLink>
          </div>
        </EmptyState>
      </>
    );
  }

  if (query.isError || !branding) {
    return (
      <>
        <PageHeader title="Settings" />
        <SettingsTabs active="domain" />
        <Banner tone="danger" title="That did not load">{errorMessage(query.error)}</Banner>
      </>
    );
  }

  const locked = !branding.mayManage || !branding.included;

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={`Where ${org.name}’s share links live.`}
      />
      <SettingsTabs active="domain" />

      <div className="space-y-6">
        {!branding.included ? (
          <Banner
            tone="caution"
            title="A custom domain comes with the team plan"
            action={
              <AppLink to="/settings/subscription">
                <Button size="sm" variant="primary">
                  See the plan
                </Button>
              </AppLink>
            }
          >
            Links are served from the Slate address until the plan is running again.
          </Banner>
        ) : !branding.mayManage ? (
          <Banner tone="info" title="Only admins and owners can change this">
            This is where {org.name}’s share links are served from.
          </Banner>
        ) : null}

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Custom domain</h2>
          <p className="mt-1 max-w-xl text-caption text-chalk-dim">
            Serve your links from your own address, so a recording you send reads as yours from the link onwards.
            Links already sent on the Slate address keep working, whatever you do here.
          </p>
          <div className="mt-4">
            <DomainSection branding={branding} orgId={org.id} disabled={locked} onSettled={settled} />
          </div>
        </section>
      </div>
    </>
  );
}
